import {booleanAttribute, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostBinding, Input, OnDestroy, OnInit, Self, signal, ViewChild} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {AbstractControl, ControlValueAccessor, FormControl, FormGroup, FormsModule, NgControl, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {MatFormFieldControl} from "@angular/material/form-field";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Subject, tap} from "rxjs";
import {BooleanInput, coerceBooleanProperty} from "@angular/cdk/coercion";
import {Mobilnummer} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {MatOptionModule} from "@angular/material/core";
import {MatSelectModule} from "@angular/material/select";
import {TranslateModule} from "@ngx-translate/core";
import {MatInputModule} from "@angular/material/input";

const debug = initDebugger('MobilnummerFeltKomponent');

export type SkjemaType = MobilnummerFeltKomponent['skjema'];
export type SkjemaVerdier = SkjemaType['value'];

/**
 * Viser skjema for angivelse av mobilnummer.
 *
 * <pre>
 * Følgende valideringsfeil eksponeres:
 * - minlength: når angitt nummer (uten forvalg) er mindre enn 3 tegn
 * - maxlength: når angitt nummer (uten forvalg) er mer enn 24 tegn
 * - pattern: når angitt nummer ikke er velformatert, se MobilnummerFeltKomponent#gyldigNummer
 *
 * Fungerer med Ng template-basert form ([(ngModel)]="...") og Ng reactive form ([formControl]="...").
 * Fungerer med <mat-form-field> fra Material.
 * </pre>
 */
@Component({
    selector: 'mobilnummer-felt',
    templateUrl: './mobilnummer-felt.html',
    styleUrls: ['./mobilnummer-felt.scss'],
    providers: [{provide: MatFormFieldControl, useExisting: MobilnummerFeltKomponent}],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MobilnummerFeltKomponent implements OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<Mobilnummer> {

    /** regexp for test av hvorvidt nummer er gyldig  */
    static gyldigNummer = /^\s*(\+|00)?[\d ]+$/;
    /** regexp for test av hvorvidt nummer starter med forvalg */
    static inkludererForvalg = /^(\+|00)(\d+)$/;

    /** Referanse til mobilnummerfelt, ikke satt før ngAfterViewInit.*/
    @ViewChild('mobilnummerfelt') mobilnummerfelt!: ElementRef<HTMLInputElement>;

    skjema = new FormGroup({
        forvalg: new FormControl(''),
        nummer: new FormControl('', {validators: [Validators.minLength(3), Validators.maxLength(24), Validators.pattern(MobilnummerFeltKomponent.gyldigNummer)]}),
    });

    tilgjengeligeForvalg = this.tilgjengeligeForvalgModeller();

    protected mobilnummerMedForvalg = signal<Mobilnummer | null>(null);

    constructor(/** skjemakontrollen tilknyttet denne komponenten via formControl eller ngModel direktiver*/
                @Self() private tilknyttetSkjemakontroll: NgControl,
                private destroyRef: DestroyRef) {
        // setter valueAccessor direkte fremfor via DI og providers i @Component med `{provide: NG_VALUE_ACCESSOR, multi:true, useExisting: MobilnummerFeltKomponent}`
        // for å unngå sirkulær avhengighet.
        this.tilknyttetSkjemakontroll.valueAccessor = this;

        this.lyttTilSkjemaEndringer();
    }

    vedFokusMottatt() {
        if (this.onTouched) this.onTouched();

        //MatFormFieldControl relatert
        if (!this.focused) {
            this.focused = true;
            this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
        }
    }

    vedFokusMistet() {
        if (this.onTouched) this.onTouched();

        //MatFormFieldControl relatert
        this.focused = false;
        if (erIkkeNil(this.onTouched)) this.onTouched();
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    nedtrekksmenyAapnet(åpen: boolean) {
        if (!åpen) this.settMarkørINummerfelt();
    }

    erValgtForvalg(a: string, b: string): boolean {
        return a === b;
    }

    //private hjelpemetoder

    private setMobilnummer(mobilnummer: Mobilnummer | null) {
        const forvalgOgNummer = this.lesNummer(mobilnummer);
        this.skjema.setValue({forvalg: forvalgOgNummer.forvalg?.nummer ?? null, nummer: forvalgOgNummer.nummer});
    }

    private lyttTilSkjemaEndringer() {
        this.skjema.valueChanges
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            tap(nummer => this.propagerValideringsfeilTilTilknyttetSkjemakontroll()),
          )
          .subscribe(skjemaverdier => {
              debug("skjema endret", skjemaverdier, this.skjema.valid)

              //sørg for å oppdatere forvalg-nedtrekksmeny dersom bruker skriver/limer inn nummer med forvalg
              const forvalgOgNummer = this.lesNummer(skjemaverdier.nummer ?? null);
              if (erIkkeNil(forvalgOgNummer.forvalg)) {
                  debug("setter forvalg", forvalgOgNummer)
                  this.skjema.setValue({forvalg: forvalgOgNummer.forvalg.nummer, nummer: forvalgOgNummer.nummer});
                  return; //endringen i skjema trigger valueChanges på nytt hvorpå mobilnummerMedForvalg blir oppdatert og verdier dermed propagerer ut
              }

              //eksponerer null dersom skjema er ugyldig, ellers mobilnummer med forvalg
              this.mobilnummerMedForvalg.set(this.beregnEksponertVerdi(skjemaverdier));
              if (this.skjema.disabled) return;
              debug("eksponerer verdi", this.mobilnummerMedForvalg(), this.skjema.valid)
              if (erIkkeNil(this.vedEndring)) this.vedEndring(this.mobilnummerMedForvalg());
          });
    }

    private beregnEksponertVerdi(skjemaverdier: SkjemaVerdier): Mobilnummer | null {
        if (this.skjema.invalid) return null;
        if (erTomEllerNil(skjemaverdier.nummer)) return null;
        if (erTomEllerNil(skjemaverdier.forvalg)) return skjemaverdier.nummer.trim();
        return `+${skjemaverdier.forvalg} ${skjemaverdier.nummer.trim()}`;
    }

    /** Sørger for at tilknyttet skjemakontroll kjører validering, som i sin tur vil kalle {@link #tilknyttetSkjemakontrollValidator} */
    private propagerValideringsfeilTilTilknyttetSkjemakontroll() {
        this.tilknyttetSkjemakontroll.control?.updateValueAndValidity({onlySelf: true, emitEvent: false})
    }

    private tilgjengeligeForvalgModeller(): Forvalg[] {
        return [{navn: "Norge (+47)", nummer: "47", indeks: 0}, {navn: "Albania (+355)", nummer: "355", indeks: 1}, {navn: "Algerie (+213)", nummer: "213", indeks: 2}, {navn: "Andorra (+376)", nummer: "376", indeks: 3}, {navn: "Angola (+244)", nummer: "244", indeks: 4}, {navn: "Anguilla (Leeward-øyene) (+1264)", nummer: "1264", indeks: 5}, {navn: "Antigua & Barbuda (Leewardøyene) (+1268)", nummer: "1268", indeks: 6}, {navn: "Argentina (+549)", nummer: "549", indeks: 7}, {navn: "Armenia (+374)", nummer: "374", indeks: 8}, {navn: "Aruba (+297)", nummer: "297", indeks: 9}, {navn: "Ascension (+247)", nummer: "247", indeks: 10}, {navn: "Aserbajdsjan (+994)", nummer: "994", indeks: 11}, {navn: "Australia (+61)", nummer: "61", indeks: 12}, {navn: "Bahamas (+1242)", nummer: "1242", indeks: 13}, {navn: "Bahrain (+973)", nummer: "973", indeks: 14}, {navn: "Bangladesh (+880)", nummer: "880", indeks: 15}, {navn: "Barbados (+1246)", nummer: "1246", indeks: 16}, {navn: "Belgia (+32)", nummer: "32", indeks: 17}, {navn: "Belize (+501)", nummer: "501", indeks: 18}, {navn: "Benin (+229)", nummer: "229", indeks: 19}, {navn: "Bermuda (+1441)", nummer: "1441", indeks: 20}, {navn: "Bhutan (+975)", nummer: "975", indeks: 21}, {navn: "Bolivia (+591)", nummer: "591", indeks: 22}, {navn: "Bosnia-Hercegovina (+387)", nummer: "387", indeks: 23}, {navn: "Botswana (+267)", nummer: "267", indeks: 24}, {navn: "Brasil (+55)", nummer: "55", indeks: 25}, {navn: "Brunei (+673)", nummer: "673", indeks: 26}, {navn: "Bulgaria (+359)", nummer: "359", indeks: 27}, {navn: "Burkina Faso (+226)", nummer: "226", indeks: 28}, {navn: "Burundi (+257)", nummer: "257", indeks: 29}, {navn: "Canada (+1)", nummer: "1", indeks: 30}, {navn: "Cayman-øyene (+1345)", nummer: "1345", indeks: 31}, {navn: "Chile (+56)", nummer: "56", indeks: 32}, {navn: "Colombia (+57)", nummer: "57", indeks: 33}, {navn: "Cook-øyene (+682)", nummer: "682", indeks: 34}, {navn: "Costa Rica (+506)", nummer: "506", indeks: 35}, {navn: "Cuba (+53)", nummer: "53", indeks: 36}, {navn: "Danmark (+45)", nummer: "45", indeks: 37}, {navn: "De Forente Arabiske Emirater (+971)", nummer: "971", indeks: 38}, {navn: "De Nederlandske Antiller (+599)", nummer: "599", indeks: 39}, {navn: "Den Demokratiske Republikk Kongo (Zaire) (+243)", nummer: "243", indeks: 40}, {navn: "Den Dominikanske Republikk (+1809)", nummer: "1809", indeks: 41}, {navn: "Den Sentralafrikanske Rep. (+236)", nummer: "236", indeks: 42}, {navn: "Diego Garcia (+246)", nummer: "246", indeks: 43}, {navn: "Djibouti (+253)", nummer: "253", indeks: 44}, {navn: "Dominica (Winward-øyene) (+1767)", nummer: "1767", indeks: 45}, {navn: "Ecuador (+593)", nummer: "593", indeks: 46}, {navn: "Egypt (+20)", nummer: "20", indeks: 47}, {navn: "Ekvatorial-Guinea (+240)", nummer: "240", indeks: 48}, {navn: "El Salvador (+503)", nummer: "503", indeks: 49}, {navn: "Elfenbenskysten (+225)", nummer: "225", indeks: 50}, {navn: "England (+44)", nummer: "44", indeks: 51}, {navn: "Eritrea (+291)", nummer: "291", indeks: 52}, {navn: "Estland (+372)", nummer: "372", indeks: 53}, {navn: "Etiopia (+251)", nummer: "251", indeks: 54}, {navn: "Falklandsøyene (+500)", nummer: "500", indeks: 55}, {navn: "Fiji (+679)", nummer: "679", indeks: 56}, {navn: "Filippinene (+63)", nummer: "63", indeks: 57}, {navn: "Finland (+358)", nummer: "358", indeks: 58}, {navn: "Frankrike (+33)", nummer: "33", indeks: 59}, {navn: "Fransk Guyana (+594)", nummer: "594", indeks: 60}, {navn: "Fransk Polynesia (+689)", nummer: "689", indeks: 61}, {navn: "Færøyene (+298)", nummer: "298", indeks: 62}, {navn: "Gabon (+241)", nummer: "241", indeks: 63}, {navn: "Gambia (+220)", nummer: "220", indeks: 64}, {navn: "Gaza & Khan Younes (+9708)", nummer: "9708", indeks: 65}, {navn: "Georgia (+995)", nummer: "995", indeks: 66}, {navn: "Ghana (+233)", nummer: "233", indeks: 67}, {navn: "Gibraltar (+350)", nummer: "350", indeks: 68}, {navn: "Grenada (Winwardøyene) (+1473)", nummer: "1473", indeks: 69}, {navn: "Grønland (+299)", nummer: "299", indeks: 70}, {navn: "Guadelope (+590)", nummer: "590", indeks: 71}, {navn: "Guam (+671)", nummer: "671", indeks: 72}, {navn: "Guatemala (+502)", nummer: "502", indeks: 73}, {navn: "Guinea (+224)", nummer: "224", indeks: 74}, {navn: "Guinea Bissau (+245)", nummer: "245", indeks: 75}, {navn: "Guyana (+592)", nummer: "592", indeks: 76}, {navn: "Haiti (+509)", nummer: "509", indeks: 77}, {navn: "Hellas (+30)", nummer: "30", indeks: 78}, {navn: "Holland (+31)", nummer: "31", indeks: 79}, {navn: "Honduras (+504)", nummer: "504", indeks: 80}, {navn: "Hong Kong (+852)", nummer: "852", indeks: 81}, {navn: "Hviterussland (+375)", nummer: "375", indeks: 82}, {navn: "India (+91)", nummer: "91", indeks: 83}, {navn: "Indonesia (+62)", nummer: "62", indeks: 84}, {navn: "Irak (+964)", nummer: "964", indeks: 85}, {navn: "Iran (+98)", nummer: "98", indeks: 86}, {navn: "Irland (+353)", nummer: "353", indeks: 87}, {navn: "Island (+354)", nummer: "354", indeks: 88}, {navn: "Israel (+972)", nummer: "972", indeks: 89}, {navn: "Italia (+39)", nummer: "39", indeks: 90}, {navn: "Jamaica (+1876)", nummer: "1876", indeks: 91}, {navn: "Japan (+81)", nummer: "81", indeks: 92}, {navn: "Jomfruøyene - Brit. (+1284)", nummer: "1284", indeks: 93}, {navn: "Jomfruøyene - US (+1340)", nummer: "1340", indeks: 94}, {navn: "Jordan (+962)", nummer: "962", indeks: 95}, {navn: "Kambodia (+855)", nummer: "855", indeks: 96}, {navn: "Kamerun (+237)", nummer: "237", indeks: 97}, {navn: "Kapp Verde-øyene (+238)", nummer: "238", indeks: 98}, {navn: "Kazakhstan (+7)", nummer: "7", indeks: 99}, {navn: "Kenya (+254)", nummer: "254", indeks: 100}, {navn: "Kina (folkerep.) (+86)", nummer: "86", indeks: 101}, {navn: "Kirghistan (+996)", nummer: "996", indeks: 102}, {navn: "Kiribati (+686)", nummer: "686", indeks: 103}, {navn: "Komorene (+269)", nummer: "269", indeks: 104}, {navn: "Kongo (+242)", nummer: "242", indeks: 105}, {navn: "Korea, Nord (+850)", nummer: "850", indeks: 106}, {navn: "Korea, Sør (+82)", nummer: "82", indeks: 107}, {navn: "Kosovo (+383)", nummer: "383", indeks: 108}, {navn: "Kroatia (+385)", nummer: "385", indeks: 109}, {navn: "Kuwait (+965)", nummer: "965", indeks: 110}, {navn: "Kypros (Nord) (+357)", nummer: "357", indeks: 111}, {navn: "Kypros (Sør) (+357)", nummer: "357", indeks: 112}, {navn: "Laos (+856)", nummer: "856", indeks: 113}, {navn: "Latvia (+371)", nummer: "371", indeks: 114}, {navn: "Lesotho (+266)", nummer: "266", indeks: 115}, {navn: "Libanon (+961)", nummer: "961", indeks: 116}, {navn: "Liberia (+231)", nummer: "231", indeks: 117}, {navn: "Libya (+218)", nummer: "218", indeks: 118}, {navn: "Liechtenstein (+41)", nummer: "41", indeks: 119}, {navn: "Litauen (+370)", nummer: "370", indeks: 120}, {navn: "Luxembourg (+352)", nummer: "352", indeks: 121}, {navn: "Macau (+853)", nummer: "853", indeks: 122}, {navn: "Madagaskar (+261)", nummer: "261", indeks: 123}, {navn: "Makedonia (+389)", nummer: "389", indeks: 124}, {navn: "Malawi (+265)", nummer: "265", indeks: 125}, {navn: "Malaysia (+60)", nummer: "60", indeks: 126}, {navn: "Maldivene (+960)", nummer: "960", indeks: 127}, {navn: "Mali (+223)", nummer: "223", indeks: 128}, {navn: "Malta (+356)", nummer: "356", indeks: 129}, {navn: "Marokko (+212)", nummer: "212", indeks: 130}, {navn: "Marshalløyene (+692)", nummer: "692", indeks: 131}, {navn: "Martinique (+596)", nummer: "596", indeks: 132}, {navn: "Mauritania (+222)", nummer: "222", indeks: 133}, {navn: "Mauritius (+230)", nummer: "230", indeks: 134}, {navn: "Mexico (+52)", nummer: "52", indeks: 135}, {navn: "Midway (+808)", nummer: "808", indeks: 136}, {navn: "Mikronesia (+691)", nummer: "691", indeks: 137}, {navn: "Moldova (+373)", nummer: "373", indeks: 138}, {navn: "Monaco (+377)", nummer: "377", indeks: 139}, {navn: "Mongolia (+976)", nummer: "976", indeks: 140}, {navn: "Montserrat (Leeward-øyene) (+1664)", nummer: "1664", indeks: 141}, {navn: "Mosambik (+258)", nummer: "258", indeks: 142}, {navn: "Myanmar (Burma) (+95)", nummer: "95", indeks: 143}, {navn: "Namibia (+264)", nummer: "264", indeks: 144}, {navn: "Nauru (+674)", nummer: "674", indeks: 145}, {navn: "Nederland (+31)", nummer: "31", indeks: 146}, {navn: "Nepal (+977)", nummer: "977", indeks: 147}, {navn: "New Zealand (+64)", nummer: "64", indeks: 148}, {navn: "Nicaragua (+505)", nummer: "505", indeks: 149}, {navn: "Niger (+227)", nummer: "227", indeks: 150}, {navn: "Nigeria (+234)", nummer: "234", indeks: 151}, {navn: "Niue (+683)", nummer: "683", indeks: 152}, {navn: "Norfolk-øyene (+672)", nummer: "672", indeks: 153}, {navn: "Ny Caledonia (+687)", nummer: "687", indeks: 154}, {navn: "Oman (+968)", nummer: "968", indeks: 155}, {navn: "Pakistan (+92)", nummer: "92", indeks: 156}, {navn: "Palau (+608)", nummer: "608", indeks: 157}, {navn: "Palestina (+970)", nummer: "970", indeks: 158}, {navn: "Panama (+507)", nummer: "507", indeks: 159}, {navn: "Papua - Ny Guinea (+675)", nummer: "675", indeks: 160}, {navn: "Paraguay (+595)", nummer: "595", indeks: 161}, {navn: "Peru (+51)", nummer: "51", indeks: 162}, {navn: "Polen (+48)", nummer: "48", indeks: 163}, {navn: "Portugal (+351)", nummer: "351", indeks: 164}, {navn: "Puerto Rico (+1787)", nummer: "1787", indeks: 165}, {navn: "Quatar (+974)", nummer: "974", indeks: 166}, {navn: "Reunion (+262)", nummer: "262", indeks: 167}, {navn: "Romania (+40)", nummer: "40", indeks: 168}, {navn: "Russland (+7)", nummer: "7", indeks: 169}, {navn: "Rwanda (+250)", nummer: "250", indeks: 170}, {navn: "Saipan (+1670)", nummer: "1670", indeks: 171}, {navn: "Salomon-øyene (+677)", nummer: "677", indeks: 172}, {navn: "Samoa - US (+684)", nummer: "684", indeks: 173}, {navn: "Samoa - West (+685)", nummer: "685", indeks: 174}, {navn: "San Marino (+378)", nummer: "378", indeks: 175}, {navn: "Sao Tome & Principe (+239)", nummer: "239", indeks: 176}, {navn: "Satellitt-Emast (+88213)", nummer: "88213", indeks: 177}, {navn: "Satellitt-GlobalStar (+8818)", nummer: "8818", indeks: 178}, {navn: "Satellitt-Inmarsat A (+870-4)", nummer: "870-4", indeks: 179}, {navn: "Satellitt-Inmarsat B/M (+870-4)", nummer: "870-4", indeks: 180}, {navn: "Satellitt-Iridium 8816 (+8816)", nummer: "8816", indeks: 181}, {navn: "Satellitt-Iridium 8817 (+8817)", nummer: "8817", indeks: 182}, {navn: "Satellitt-MCP (+88232)", nummer: "88232", indeks: 183}, {navn: "Satellitt-Thuarya (+88216)", nummer: "88216", indeks: 184}, {navn: "Saudi Arabia (+966)", nummer: "966", indeks: 185}, {navn: "Senegal (+221)", nummer: "221", indeks: 186}, {navn: "Serbia og Montenegro (+381)", nummer: "381", indeks: 187}, {navn: "Seychellene (+248)", nummer: "248", indeks: 188}, {navn: "Sierra Leone (+232)", nummer: "232", indeks: 189}, {navn: "Singapore (+65)", nummer: "65", indeks: 190}, {navn: "Slovakia (+421)", nummer: "421", indeks: 191}, {navn: "Slovenia (+386)", nummer: "386", indeks: 192}, {navn: "Somalia (+252)", nummer: "252", indeks: 193}, {navn: "Spania (+34)", nummer: "34", indeks: 194}, {navn: "Sri Lanka (+94)", nummer: "94", indeks: 195}, {navn: "St. Helena (+290)", nummer: "290", indeks: 196}, {navn: "St. Kitts & Nevis (Leeward-øyene) (+1869)", nummer: "1869", indeks: 197}, {navn: "St. Lucia (Winward-øyene) (+1758)", nummer: "1758", indeks: 198}, {navn: "St. Pierre & Miquelon (+508)", nummer: "508", indeks: 199}, {navn: "St. Vincent (Winward-øyene) (+1809)", nummer: "1809", indeks: 200}, {navn: "Storbritannia (+44)", nummer: "44", indeks: 201}, {navn: "Sudan (+249)", nummer: "249", indeks: 202}, {navn: "Surinam (+597)", nummer: "597", indeks: 203}, {navn: "Sveits (+41)", nummer: "41", indeks: 204}, {navn: "Sverige (+46)", nummer: "46", indeks: 205}, {navn: "Swaziland (+268)", nummer: "268", indeks: 206}, {navn: "Syria (+963)", nummer: "963", indeks: 207}, {navn: "Sør-Afrika Republikken (+27)", nummer: "27", indeks: 208}, {navn: "Sør-Sudan (+211)", nummer: "211", indeks: 209}, {navn: "Tadzjikistan (+992)", nummer: "992", indeks: 210}, {navn: "Taiwan (+886)", nummer: "886", indeks: 211}, {navn: "Tanzania (+255)", nummer: "255", indeks: 212}, {navn: "Thailand (+66)", nummer: "66", indeks: 213}, {navn: "Togo (+228)", nummer: "228", indeks: 214}, {navn: "Tonga (+676)", nummer: "676", indeks: 215}, {navn: "Trinidad & Tobago (+1868)", nummer: "1868", indeks: 216}, {navn: "Tristan da Cunha (+2908)", nummer: "2908", indeks: 217}, {navn: "Tsjad (+235)", nummer: "235", indeks: 218}, {navn: "Tsjekkia (+420)", nummer: "420", indeks: 219}, {navn: "Tunisia (+216)", nummer: "216", indeks: 220}, {navn: "Turcs- & Caicos-øyene (+1649)", nummer: "1649", indeks: 221}, {navn: "Turkmenistan (+993)", nummer: "993", indeks: 222}, {navn: "Tuvalu (+688)", nummer: "688", indeks: 223}, {navn: "Tyrkia (+90)", nummer: "90", indeks: 224}, {navn: "Tyskland (+49)", nummer: "49", indeks: 225}, {navn: "Uganda (+256)", nummer: "256", indeks: 226}, {navn: "Ukraina (+380)", nummer: "380", indeks: 227}, {navn: "Ungarn (+36)", nummer: "36", indeks: 228}, {navn: "Uruguay (+598)", nummer: "598", indeks: 229}, {navn: "USA (+1)", nummer: "1", indeks: 230}, {navn: "Uzbekistan (+998)", nummer: "998", indeks: 231}, {navn: "Vanuatu (+678)", nummer: "678", indeks: 232}, {navn: "Venezuela (+58)", nummer: "58", indeks: 233}, {navn: "Vietnam (+84)", nummer: "84", indeks: 234}, {navn: "Yemen (+967)", nummer: "967", indeks: 235}, {navn: "Zambia (+260)", nummer: "260", indeks: 236}, {navn: "Zimbabwe (+263)", nummer: "263", indeks: 237}, {navn: "Østerrike (+43)", nummer: "43", indeks: 238}]
    }

    /** Splitter angitt nummer i forvalgsdel og nummer(uten forvalg).
     * Merk: public for testbarhet */
    lesNummer(nummer: Mobilnummer | null): LesNummerResultat {
        const resultat: LesNummerResultat = {forvalg: null, nummer: nummer};
        if (erTomEllerNil(nummer)) return resultat;

        const nummerUtenBlanke = nummer.replace(/\s/g, '');
        const harForvalg = MobilnummerFeltKomponent.inkludererForvalg.exec(nummerUtenBlanke);
        if (erIkkeNil(harForvalg)) {
            const landkodeprefix = harForvalg[1]; //ledende '+' eller '00'
            const nummerUtenLandkodeprefix = harForvalg[2]; //uten ledende '+' eller '00'
            const prefiks = this.tilgjengeligeForvalg.find(prefiks => nummerUtenLandkodeprefix.startsWith(prefiks.nummer))
            if (erNil(prefiks)) {
                debug('nummer har ukjent forvalg', nummerUtenLandkodeprefix);
                return resultat;
            }
            resultat.forvalg = prefiks;
            //fjern prefix og landkode fra nummer
            Array.from(`${landkodeprefix}${prefiks.nummer}`).forEach(tegn => nummer = nummer!.replace(tegn, ''));
            resultat.nummer = nummer.trim();
        }

        return resultat;
    }

    private settMarkørINummerfelt() {
        this.mobilnummerfelt.nativeElement.focus();

    }

    //ng2 livssykelmetoder

    ngOnInit(): void {
        //sørger for at valideringsfeil knyttet til det lokale skjemaet propagerer til skjemakontroll tilknyttet denne komponenten
        //kan ikke gjøres i konstruktør da tilknyttetSkjemakontroll.control ikke er satt enda
        this.tilknyttetSkjemakontroll.control!.addValidators(this.tilknyttetSkjemakontrollValidator())
    }

    /** Returnerer valideringsfeil registrert på {@link #skjema}.
     * Se også {@link #propagerValideringsfeilTilTilknyttetSkjemakontroll} */
    private tilknyttetSkjemakontrollValidator(): ValidatorFn {
        return (control: AbstractControl<Mobilnummer>): ValidationErrors | null => {
            //les valideringsfeil fra feltKontroll og returner disse
            debug("tilknyttetSkjemakontrollValidator", this.skjema.valid, this.skjema.controls.forvalg.errors, this.skjema.controls.nummer.errors)
            return this.skjema.controls.nummer.errors;
        }
    }

    ngOnDestroy() {
        this.stateChanges.complete();
    }


    //ControlValueAccessor implementasjon

    /** Settes via {@link #registerOnChange} (ifbm ControlValueAccessor)*/
    private vedEndring: Function | null = null;

    /** Settes via {@link #registerOnTouched} (ifbm ControlValueAccessor)*/
    private onTouched: Function | null = null;

    registerOnChange(vedEndring: Function): void {
        this.vedEndring = vedEndring;
    }

    registerOnTouched(vedBerørt: Function): void {
        this.onTouched = vedBerørt;
    }

    setDisabledState(isDisabled: boolean): void {
        debug("setDisabledState", isDisabled)
        if (isDisabled) this.skjema.disable();
        else this.skjema.enable();
    }

    writeValue(mobilnummer: Mobilnummer | null): void {
        debug("writeValue", mobilnummer)
        this.setMobilnummer(mobilnummer)
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    //MatFormFieldControl implementasjon

    /** brukes til å lage unike IDer, se {@link #id}*/
    static nesteId = 0;

    private _placeholder: string = '';
    private _required = false;

    @Input('aria-describedby') userAriaDescribedBy!: string; //@todo hva gjør vi med denne?

    get placeholder() {
        return this.shouldLabelFloat ? this._placeholder : '';
    }

    @Input() set placeholder(plh) {
        this._placeholder = plh;
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    get required() {
        return this._required ||
          (this.tilknyttetSkjemakontroll.control?.hasValidator(Validators.required) ?? false);
    }

    @Input({transform: booleanAttribute}) set required(req: boolean) {
        this._required = req;
        if (req) this.skjema.controls.nummer.addValidators(Validators.required);
        else this.skjema.controls.nummer.removeValidators(Validators.required);
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    get disabled(): boolean {
        return this.skjema.disabled
    }

    @Input() set disabled(value: BooleanInput) {
        this.setDisabledState(coerceBooleanProperty(value))
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    public get ngControl(): NgControl {
        return this.tilknyttetSkjemakontroll;
    }

    readonly stateChanges = new Subject<void>();

    @HostBinding() id = `mobilnummer-felt-${MobilnummerFeltKomponent.nesteId++}`;

    controlType = 'mobilnummer-felt';

    /** se {@link #vedFokusMottatt} / {@link #vedFokusMistet} */
    focused = false;

    get value(): Mobilnummer | null {
        return this.mobilnummerMedForvalg();
    }

    set value(klokkeslett: Mobilnummer | null) {
        this.writeValue(klokkeslett);
    }

    get empty() {
        return erTomEllerNil(this.skjema.value.forvalg) && erTomEllerNil(this.skjema.value.nummer);
    }

    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    get errorState(): boolean {
        return this.skjema.invalid;
    }

    setDescribedByIds(ids: string[]) {
        if (erNil(this.mobilnummerfelt)) return;
        const controlElement = this.mobilnummerfelt.nativeElement;
        controlElement.setAttribute('aria-describedby', ids.join(' '));
    }

    onContainerClick(event: MouseEvent) {
        if ((event.target as Element).tagName.toLowerCase() == 'input') return; //trenger ikke å sette fokus dersom klikket var i inputfeltet
        if (erNil(this.mobilnummerfelt)) return;

        this.settMarkørINummerfelt();
    }
}

interface Forvalg {
    navn: string;
    nummer: string;
    indeks: number;
}

interface LesNummerResultat {
    forvalg: Forvalg | null;
    nummer: string | null;
}
